<template>
  <b-container
    fluid
    class="bg-white"
    v-if="HasAccessToTab('all-assets') || HasAccessToTab('us-assets')">
    <div class="row" v-if="!IsOnAssetBoardClientViewMode">
      <div class="col-lg lizard-form-empty-col"></div>
    </div>

    <!-- ===== ACTION BUTTONS ROW ===== -->
    <!-- Uses the same col-lg-12 wrapper as the filter panel below so the
         tabs' left edge aligns exactly with the panel, letting them dock
         onto it flush instead of floating at a different offset. -->
    <div class="row" v-if="!IsOnAssetBoardClientViewMode">
      <div class="col-lg-12 lizard-action-row">
        <ul class="lizard-asset-tabs-list">
          <li v-if="HasAccessToTab('all-assets')">
            <div
              class="lizard-asset-tab"
              :class="{ active: assetBoardTab === 'all' }"
              @click="assetBoardTab = 'all'">
              All Assets
            </div>
          </li>
          <li v-if="HasAccessToTab('us-assets')">
            <div
              class="lizard-asset-tab"
              :class="{ active: assetBoardTab === 'us' }"
              @click="assetBoardTab = 'us'">
              US Assets
            </div>
          </li>
        </ul>
        <ul
          class="lizard-assetboard-btns-list"
          v-if="HasAccessToTab('all-assets')">
          <li>
            <div
              v-b-tooltip.hover.bottom="{ variant: 'info' }"
              title="Switch to Client View"
              @click="toggleClientView"
              style="max-width: 120px !important"
              class="lizard-client-view-btn"
              :class="{ 'lizard-monday-sync': loadingAssetBoard }">
              Client View
            </div>
          </li>
          <li v-if="SelectedAssets.length || RejectedAssets.length">
            <download-excel
              v-b-tooltip.hover.bottom="{ variant: 'success' }"
              title="Export Reserved Assets"
              class="lizard-export-excel-btn"
              style="max-width: 180px !important"
              :class="{ 'lizard-monday-sync': loadingAssetBoard }"
              :before-generate="generateReservedAssetsExcel"
              :before-finish="resetExportData"
              :type="exportType"
              :name="exportName"
              :fields="exportFields"
              :data="exportData"
              :worksheet="exportWorksheet"
              :header-style="{
                font: { bold: true, color: { rgb: 'FFFFFF' }, name: 'Calibri' },
                fill: { fgColor: { rgb: '0070C0' } },
                alignment: { horizontal: 'left', vertical: 'bottom' }
              }"
              :column-widths="[
                9.86, 19.86, 21.86, 9.86, 11.86, 44.86, 12.86, 12.86, 10.86
              ]">
              <b-button
                variant="success"
                size="sm"
                style="width: 100% !important"
                ><b-icon
                  icon="file-earmark-excel-fill"
                  title="Export Excel"></b-icon>
                Export Selected Assets</b-button
              >
            </download-excel>
          </li>
          <li v-if="exportExcel">
            <download-excel
              v-b-tooltip.hover.bottom="{ variant: 'success' }"
              title="Export Excel"
              class="lizard-export-excel-btn"
              style="max-width: 120px !important"
              :class="{ 'lizard-monday-sync': loadingAssetBoard }"
              :before-generate="generateExcel"
              :before-finish="resetExportData"
              :type="exportType"
              :name="exportName"
              :fields="exportFields"
              :data="exportData"
              :worksheet="exportWorksheet"
              :header-style="{
                font: { bold: true, color: { rgb: 'FFFFFF' }, name: 'Calibri' },
                fill: { fgColor: { rgb: '0070C0' } },
                alignment: { horizontal: 'left', vertical: 'bottom' }
              }"
              :column-widths="[
                19.86, 21.86, 9.86, 11.86, 44.86, 12.86, 12.86, 10.86
              ]">
              <b-button
                variant="success"
                size="sm"
                style="width: 100% !important"
                ><b-icon
                  icon="file-earmark-excel-fill"
                  title="Export Excel"></b-icon>
                Export Excel</b-button
              >
            </download-excel>
          </li>
          <li v-if="HasAdminPerm">
            <div
              v-b-tooltip.hover.bottom="{ variant: 'info' }"
              title="Sync with Monday.com"
              class="lizard-monday-btn"
              :class="{ 'lizard-monday-sync': loadingAssetBoard }"
              style="max-width: 120px !important"
              @click="fetchFromMonday">
              <img
                src="../../assets/monday-1.jpg"
                class="lizard-monday-button" />
            </div>
          </li>
        </ul>
      </div>
    </div>

    <div class="row lizard-exit-client-view-row" v-else>
      <div class="lizard-col-padding text-right">
        <div
          v-b-tooltip.hover.bottom="{ variant: 'info' }"
          title="Exit Client View"
          style="cursor: pointer"
          @click="toggleClientView">
          <b-icon icon="x-circle"></b-icon>
        </div>
      </div>
    </div>

    <!-- ===== FILTER PANEL ===== -->
    <div class="row">
      <div class="col-lg-12">
        <b-card bg-variant="light" class="lizard-filter-panel-card">
          <!-- Filter Labels Row -->
          <div class="row lizard-filters-row">
            <div class="col-lg text-left lizard-col-padding">
              <span class="filter-label">
                <b-icon-search font-scale="0.8"></b-icon-search>&nbsp;
                <span class="filter-label-text">asset</span>&nbsp;
                <span class="filter-label-hint">(Search)</span>
              </span>
            </div>
            <div class="col-lg text-left lizard-col-padding">
              <span class="filter-label">
                <b-icon-list-ul font-scale="0.8"></b-icon-list-ul>&nbsp;
                <span class="filter-label-text">subsector</span>&nbsp;
                <span class="filter-label-hint">(List)</span>
              </span>
            </div>
            <div
              class="col-lg text-left lizard-col-padding"
              v-if="!IsOnAssetBoardClientViewMode">
              <span class="filter-label">
                <b-icon-list-ul font-scale="0.8"></b-icon-list-ul>&nbsp;
                <span class="filter-label-text">process</span>&nbsp;
                <span class="filter-label-hint">(List)</span>
              </span>
            </div>
            <div
              class="lizard-toggle-col-minority text-left lizard-col-padding">
              <span class="filter-label">
                <b-icon-people font-scale="0.8"></b-icon-people>&nbsp;
                <span class="filter-label-text">minorities</span>
              </span>
            </div>
            <div class="lizard-toggle-col-infra text-left lizard-col-padding">
              <span class="filter-label">
                <b-icon-hdd-network font-scale="0.8"></b-icon-hdd-network>&nbsp;
                <span class="filter-label-text">infra</span>
              </span>
            </div>
            <div class="col-lg text-left lizard-col-padding">
              <span class="filter-label">
                <b-icon-list-ul font-scale="0.8"></b-icon-list-ul>&nbsp;
                <span class="filter-label-text">launch</span>&nbsp;
                <span class="filter-label-hint">(List)</span>
              </span>
            </div>
            <div class="col-lg text-left lizard-col-padding">
              <span class="filter-label">
                <b-icon-list-ul font-scale="0.8"></b-icon-list-ul>&nbsp;
                <span class="filter-label-text">last update</span>&nbsp;
                <span class="filter-label-hint">(List)</span>
              </span>
            </div>
            <div class="col-lg text-left lizard-col-padding">
              <span class="filter-label">
                <b-icon-list-ul font-scale="0.8"></b-icon-list-ul>&nbsp;
                <span class="filter-label-text">est. ebitda</span>&nbsp;
                <span class="filter-label-hint">(Select)</span>
              </span>
            </div>
            <div class="col-lg text-left lizard-col-padding">
              <span class="filter-label">
                <b-icon-search font-scale="0.8"></b-icon-search>&nbsp;
                <span class="filter-label-text">ibank</span>&nbsp;
                <span class="filter-label-hint">(Search)</span>
              </span>
            </div>
            <div
              class="col-lg text-left lizard-col-padding"
              v-if="assetBoardTab !== 'us'">
              <span class="filter-label">
                <b-icon-list-ul font-scale="0.8"></b-icon-list-ul>&nbsp;
                <span class="filter-label-text">hq country</span>&nbsp;
                <span class="filter-label-hint">(List)</span>
              </span>
            </div>
            <div class="lizard-action-col text-left lizard-col-padding">
              &nbsp;
            </div>
          </div>

          <!-- Filter Inputs Row -->
          <div class="row lizard-filters-row">
            <!-- Asset Search -->
            <div class="col-lg text-left lizard-col-padding">
              <b-form-input
                size="sm"
                placeholder="Type and select..."
                v-model="pendingFilters.asset.val"
                @keyup.enter="stagePendingAssetFilter"></b-form-input>
            </div>

            <!-- Subsector TreeSelect -->
            <div class="col-lg text-left lizard-col-padding">
              <div
                class="d-flex justify-content-center mb-3"
                v-if="!metas.subsectors">
                <b-button size="sm" variant="light" disabled>
                  <b-spinner small type="grow" label="Loading..."></b-spinner>
                  Loading...
                </b-button>
              </div>
              <treeselect
                v-else
                placeholder="Type and select..."
                v-model="pendingFilters.subsector.treeValue"
                :multiple="subsectorTreeSettings.multiple"
                :clearable="subsectorTreeSettings.clearable"
                :searchable="subsectorTreeSettings.searchable"
                :open-on-click="subsectorTreeSettings.openOnClick"
                :close-on-select="subsectorTreeSettings.closeOnSelect"
                :limit="subsectorTreeSettings.limit"
                :options="subsectorTreeSettings.options" />
            </div>

            <!-- Process Dropdown -->
            <div
              class="col-lg text-left lizard-col-padding"
              v-if="!IsOnAssetBoardClientViewMode">
              <div
                class="d-flex justify-content-center mb-3"
                v-if="!metas.assetsprocess">
                <b-button size="sm" variant="light" disabled>
                  <b-spinner small type="grow" label="Loading..."></b-spinner>
                  Loading...
                </b-button>
              </div>
              <b-form-select
                v-else
                v-model="pendingFilters.process.val"
                :options="metas.assetsprocess"
                placeholder="Process..."
                size="sm"></b-form-select>
            </div>

            <!-- Minorities Toggle Filter -->
            <div
              class="lizard-toggle-col-minority text-left lizard-col-padding">
              <b-form-checkbox
                v-model="pendingFilters.minority.val"
                size="sm"
                button
                button-variant="outline-secondary"
                class="lizard-minority-radio"
                >Minorities</b-form-checkbox
              >
            </div>

            <!-- Infra Toggle Filter -->
            <div class="lizard-toggle-col-infra text-left lizard-col-padding">
              <b-form-checkbox
                v-model="pendingFilters.infra.val"
                size="sm"
                button
                button-variant="outline-secondary"
                class="lizard-minority-radio"
                >Infra</b-form-checkbox
              >
            </div>

            <!-- Launch Dropdown -->
            <div class="col-lg text-left lizard-col-padding">
              <div
                class="d-flex justify-content-center mb-3"
                v-if="!metas.assetslaunchtags">
                <b-button size="sm" variant="light" disabled>
                  <b-spinner small type="grow" label="Loading..."></b-spinner>
                  Loading...
                </b-button>
              </div>
              <b-form-select
                v-else
                v-model="pendingFilters.launch.val"
                :options="metas.assetslaunchtags"
                placeholder="Launch..."
                size="sm"></b-form-select>
            </div>

            <!-- Last Update Dropdown -->
            <div class="col-lg text-left lizard-col-padding">
              <div
                class="d-flex justify-content-center mb-3"
                v-if="!metas.assetslastupdate">
                <b-button size="sm" variant="light" disabled>
                  <b-spinner small type="grow" label="Loading..."></b-spinner>
                  Loading...
                </b-button>
              </div>
              <b-form-select
                v-else
                v-model="pendingFilters.lastupdate.val"
                :options="metas.assetslastupdate"
                placeholder="Last updated..."
                size="sm"></b-form-select>
            </div>

            <!-- EBITDA Slider -->
            <div class="col-lg text-left lizard-col-padding">
              <div class="d-flex justify-content-center mb-3">
                <vue-slider
                  v-model="pendingFilters.ebitda.range"
                  :width="slider.width"
                  :min="slider.minRange"
                  :max="slider.maxRange"
                  :height="slider.height"
                  :tooltip="slider.tooltip"
                  :tooltipPlacement="slider.tooltipPlacement"
                  :duration="slider.duration"
                  :interval="slider.interval"
                  :lazy="slider.lazy"
                  :process-style="{ 'background-color': '#999' }">
                  <template v-slot:tooltip="{ value }">
                    <div class="lizard-slider-tooltip">
                      {{ getSliderTooltipValue(value) }}
                    </div>
                  </template>
                </vue-slider>
              </div>
            </div>

            <!-- IBank Search -->
            <div class="col-lg text-left lizard-col-padding">
              <b-form-input
                size="sm"
                placeholder="Type and select..."
                v-model="pendingFilters.ibank.val"
                @keyup.enter="stagePendingIBankFilter"></b-form-input>
            </div>

            <!-- HQ Country TreeSelect -->
            <div
              class="col-lg text-left lizard-col-padding"
              v-if="assetBoardTab !== 'us'">
              <div
                class="d-flex justify-content-center mb-3"
                v-if="!metas.hqcountriesNew">
                <b-button size="sm" variant="light" disabled>
                  <b-spinner small type="grow" label="Loading..."></b-spinner>
                  Loading...
                </b-button>
              </div>
              <treeselect
                v-else
                placeholder="Type and select..."
                v-model="pendingFilters.hqcountry.treeValue"
                :multiple="hqcountryTreeSettings.multiple"
                :clearable="hqcountryTreeSettings.clearable"
                :searchable="hqcountryTreeSettings.searchable"
                :open-on-click="hqcountryTreeSettings.openOnClick"
                :close-on-select="hqcountryTreeSettings.closeOnSelect"
                :limit="hqcountryTreeSettings.limit"
                :options="hqcountryTreeSettings.options" />
            </div>

            <!-- Action Buttons -->
            <div class="lizard-action-col text-left lizard-col-padding">
              <div class="filter-action-buttons-horizontal">
                <!-- Search Button -->
                <div
                  v-b-tooltip.hover.bottom="{ variant: 'success' }"
                  title="Apply filters and search"
                  @click="applyFilters"
                  class="lizard-search-btn-inline"
                  :class="{ 'lizard-monday-sync': loadingAssetBoard }">
                  <b-icon icon="search"></b-icon>&nbsp;Search
                </div>

                <!-- Clear Filters Button -->
                <div
                  v-b-tooltip.hover.bottom="{ variant: 'info' }"
                  title="Clear all filters"
                  @click="clearFilters"
                  class="lizard-clear-filters-btn-inline"
                  :class="{ 'lizard-monday-sync': loadingAssetBoard }">
                  <b-icon icon="x-circle"></b-icon>&nbsp;Clear
                </div>
              </div>
            </div>
          </div>

          <!-- Filter Tags Row -->
          <div class="row lizard-filters-row">
            <!-- Asset Filter Tags -->
            <div class="col-lg text-left lizard-col-padding">
              <ul class="filterTags">
                <li
                  v-for="(item, idx) in appliedFilters.asset"
                  :key="'asset-' + idx">
                  <b-badge
                    @click="removeAppliedFilter('asset', item)"
                    variant="info"
                    class="lizard-setting-badge"
                    title="Click to remove this filter"
                    v-b-tooltip.hover.bottom="{ variant: 'secondary' }">
                    <b-icon icon="x-circle-fill"></b-icon>
                    {{ item }}
                  </b-badge>
                </li>
                <li
                  v-for="(item, idx) in stagedFilters.asset"
                  :key="'asset-staged-' + idx">
                  <b-badge
                    @click="stagePendingAssetFilter(item)"
                    variant="secondary"
                    class="lizard-setting-badge lizard-setting-badge-temp"
                    title="Click to add this filter"
                    v-b-tooltip.hover.bottom="{ variant: 'info' }">
                    {{ item }}
                  </b-badge>
                </li>
              </ul>
            </div>

            <div class="col-lg text-left lizard-col-padding"></div>
            <div
              class="col-lg text-left lizard-col-padding"
              v-if="!IsOnAssetBoardClientViewMode"></div>
            <div
              class="lizard-toggle-col-minority text-left lizard-col-padding"></div>
            <div
              class="lizard-toggle-col-infra text-left lizard-col-padding"></div>
            <div class="col-lg text-left lizard-col-padding"></div>
            <div class="col-lg text-left lizard-col-padding"></div>
            <div class="col-lg text-left lizard-col-padding"></div>

            <!-- IBank Filter Tags -->
            <div class="col-lg text-left lizard-col-padding">
              <ul class="filterTags">
                <li
                  v-for="(item, idx) in appliedFilters.ibank"
                  :key="'ibank-' + idx">
                  <b-badge
                    @click="removeAppliedFilter('ibank', item)"
                    variant="info"
                    class="lizard-setting-badge"
                    title="Click to remove this filter"
                    v-b-tooltip.hover.bottom="{ variant: 'secondary' }">
                    <b-icon icon="x-circle-fill"></b-icon>
                    {{ item }}
                  </b-badge>
                </li>
                <li
                  v-for="(item, idx) in stagedFilters.ibank"
                  :key="'ibank-staged-' + idx">
                  <b-badge
                    @click="stagePendingIBankFilter(item)"
                    variant="secondary"
                    class="lizard-setting-badge lizard-setting-badge-temp"
                    title="Click to add this filter"
                    v-b-tooltip.hover.bottom="{ variant: 'info' }">
                    {{ item }}
                  </b-badge>
                </li>
              </ul>
            </div>

            <div
              class="col-lg text-left lizard-col-padding"
              v-if="assetBoardTab !== 'us'"></div>
            <div class="lizard-action-col text-left lizard-col-padding">
              &nbsp;
            </div>
          </div>
        </b-card>
      </div>
    </div>

    <!-- ===== TABLE VIEW ===== -->
    <div class="row">
      <div class="col-lg-12">
        <table-view
          :loading="loadingAssetBoard"
          :listItems="listItems"
          :on-client-view="IsOnAssetBoardClientViewMode"
          :hide-hq-country="assetBoardTab === 'us'"></table-view>
      </div>
    </div>

    <div class="row">
      <div class="col-lg lizard-form-empty-col"></div>
    </div>
  </b-container>
  <div class="container text-center" v-else>
    <h3>
      <b-badge variant="warning"
        >Access to this section is restricted for your account.</b-badge
      >
    </h3>
  </div>
</template>

<script>
// ===== IMPORTS & DEPENDENCIES =====
import Treeselect from '@riophae/vue-treeselect'
import VueSlider from 'vue-slider-component'
import 'vue-slider-component/theme/default.css'
import Services from '../../services/main.service.vue'
import TableView from './asset-board-components/table-view.vue'
import AssetsService from '../../services/assets.service.vue'

// ===== CONFIGURATION & CONSTANTS =====
const FILTER_SUGGESTION_LIMIT = 3
const EBITDA_MIN = 0
const EBITDA_MAX = 100
const EBITDA_INTERVAL = 5
const US_HQ_COUNTRY_VALUE = 'AMERICAS:USA'

// ===== COMPONENT DEFINITION =====
export default {
  name: 'AssetBoard',

  components: {
    Treeselect,
    VueSlider,
    TableView
  },

  data() {
    return {
      // Loading states
      loadingAssetBoard: false,
      loadingText: 'Loading...',
      loadingStatus: 'light',

      // Asset board tab group
      prevHqCountryTreeValue: null,

      // Metadata from API
      metas: {},

      // Table data
      listItems: [],
      ebitdaList: [],
      allReservedAssets: [],

      // Pending filters (not yet applied - staging area)
      pendingFilters: {
        asset: { val: '', treeValue: null },
        subsector: { val: '', treeValue: null },
        process: { val: '', treeValue: null },
        ebitda: { range: [EBITDA_MIN, EBITDA_MAX] },
        ibank: { val: '', treeValue: null },
        hqcountry: { val: '', treeValue: null },
        launch: { val: '', treeValue: null },
        lastupdate: { val: '', treeValue: null },
        minority: { val: false, treeValue: null },
        infra: { val: false, treeValue: null }
      },

      // Staged filters (suggestions from search)
      stagedFilters: {
        asset: [],
        subsector: [],
        process: [],
        ebitda: [],
        ibank: [],
        hqcountry: [],
        launch: [],
        lastupdate: []
      },

      // Applied filters (currently active)
      appliedFilters: {
        asset: [],
        subsector: [],
        process: [],
        ebitda: [],
        ibank: [],
        hqcountry: [],
        launch: [],
        lastupdate: []
      },

      // TreeSelect settings
      subsectorTreeSettings: {
        value: null,
        multiple: true,
        clearable: true,
        searchable: true,
        openOnClick: true,
        closeOnSelect: true,
        limit: 3,
        options: []
      },

      hqcountryTreeSettings: {
        value: null,
        multiple: true,
        clearable: true,
        searchable: true,
        openOnClick: true,
        closeOnSelect: true,
        limit: 3,
        options: []
      },

      // Slider configuration
      slider: {
        width: '85%',
        size: 1,
        minRange: EBITDA_MIN,
        maxRange: EBITDA_MAX,
        duration: 2,
        interval: EBITDA_INTERVAL,
        lazy: true,
        height: '14px',
        tooltip: 'always',
        tooltipPlacement: 'bottom'
      },

      // Export configuration
      exportExcel: true,
      exportType: 'xlsx',
      exportName: 'assetboard-data.xlsx',
      exportWorksheet: 'Assetboard Data',
      exportFields: {
        Status: 'status',
        Asset: 'asset',
        Subsector: 'subsector',
        Launch: 'launch',
        IBank: 'ibank',
        Description: 'description',
        'Est EBITDA': 'ebitda',
        Owner: 'owner',
        'HQ Country': 'hqcountry'
      },
      exportData: [],

      /// EMAIL
      reportEmailSent: false
    }
  },

  // ===== COMPUTED PROPERTIES =====
  computed: {
    Store() {
      return this.$store.getters
    },

    UserData() {
      return this.Store.userAuth
    },

    HasAdminPerm() {
      return this.$store.state.user.hasAdminPrivileges
    },

    Pager() {
      return this.Store.assetBoardPager
    },

    Filters() {
      return this.Store.assetBoardFilters
    },

    SortOrders() {
      return this.Store.assetBoardSort.join(',')
    },

    IsOnAssetBoardClientViewMode() {
      return this.Store.assetBoardClientView
    },

    // Backed by the store so the tab selection survives the Client View
    // component remount (Home.vue mounts a separate AssetBoard instance
    // for client view rather than reusing the same one).
    assetBoardTab: {
      get() {
        return this.Store.assetBoardTab
      },
      set(tab) {
        this.$store.commit('setAssetBoardTab', tab)
      }
    },

    SelectedAssets() {
      return this.Store.assetBoardSelectedAssets
    },

    RejectedAssets() {
      return this.Store.assetBoardRejectedAssets
    },

    ReservedAssets() {
      return [
        ...this.Store.assetBoardSelectedAssets,
        ...this.Store.assetBoardRejectedAssets
      ]
    }
  },

  // ===== LIFECYCLE HOOKS =====
  mounted() {
    this.initializeComponent()
    this.registerEventListeners()
  },

  // Note: Vue 2 uses beforeDestroy (Vue 3 uses beforeUnmount)
  // eslint-disable-next-line vue/no-deprecated-destroyed-lifecycle
  beforeDestroy() {
    this.unregisterEventListeners()
  },

  // ===== METHODS =====
  methods: {
    // ===== INITIALIZATION METHODS =====

    /**
     * Initialize component on mount
     */
    initializeComponent() {
      // Auto-select US Assets tab when the All Assets tab is hidden.
      // Set the HQ Country filter directly instead of relying on the
      // assetBoardTab watcher: its applyFilters() bails while the initial
      // fetch is loading, so the US filter would never get applied.
      if (
        !this.HasAccessToTab('all-assets') &&
        this.HasAccessToTab('us-assets') &&
        this.assetBoardTab !== 'us'
      ) {
        this.assetBoardTab = 'us'
        this.pendingFilters.hqcountry.treeValue = [US_HQ_COUNTRY_VALUE]
        this.$store.commit('setAssetBoardFilters', {
          ...this.Filters,
          hqcountry: [US_HQ_COUNTRY_VALUE]
        })
      }
      this.fetchMetas()
      this.syncPendingFiltersWithStore()
      this.fetchData({ monday: false })
    },

    /**
     * Register event listeners
     */
    registerEventListeners() {
      this.$root.$on('fetch_table_view_data', this.handleFetchTableViewData)
    },

    /**
     * Unregister event listeners
     */
    unregisterEventListeners() {
      this.$root.$off('fetch_table_view_data', this.handleFetchTableViewData)
    },

    HasAccessToTab(tabId) {
      const UserEmail = this.UserData.details.email
      const AllowedEmails = [
        'cfoubister@candesic.com',
        'cteixidor@candesic.com',
        'amitchell@candesic.com',
        'amirhosseini@candesic.com'
      ]
      const DeniedEmails = [
        'cfoubister@candesic.com'
        // 'amirhosseini@candesic.com'
      ]
      if (tabId === 'us-assets') {
        return AllowedEmails.includes(UserEmail.toLowerCase())
      }
      return !DeniedEmails.includes(UserEmail.toLowerCase())
    },

    /**
     * Handle fetch table view data event
     */
    handleFetchTableViewData() {
      this.fetchData({ monday: false })
    },

    /**
     * Sync pending filters with store state
     */
    syncPendingFiltersWithStore() {
      const storeFilters = this.Filters

      // Sync applied filters
      Object.keys(this.appliedFilters).forEach((key) => {
        if (storeFilters[key] && Array.isArray(storeFilters[key])) {
          this.appliedFilters[key] = [...storeFilters[key]]
        }
      })

      // Sync EBITDA range
      if (storeFilters.ebitda && Array.isArray(storeFilters.ebitda)) {
        this.pendingFilters.ebitda.range = [...storeFilters.ebitda]
      }

      // Sync tree selects
      if (storeFilters.subsector) {
        this.pendingFilters.subsector.treeValue = storeFilters.subsector
      }
      if (storeFilters.hqcountry) {
        this.pendingFilters.hqcountry.treeValue = storeFilters.hqcountry
      }
    },

    // ===== FILTER APPLICATION METHODS =====

    /**
     * Apply all pending filters and trigger data fetch
     */
    async applyFilters() {
      if (this.loadingAssetBoard) return

      this.loadingAssetBoard = true
      this.listItems = []

      try {
        const filterPayload = this.buildFilterPayload()

        // Update store with new filters
        this.$store.commit('setAssetBoardFilters', filterPayload)
        this.$store.commit('setAssetBoardPager', { page: 1 })

        // Update applied filters state
        this.updateAppliedFilters()

        // Clear staged suggestions
        this.clearStagedFilters()

        // Fetch data with new filters
        await this.fetchData({ monday: false })
      } catch (error) {
        console.error('Error applying filters:', error)
        this.showToast('Error applying filters', 'danger')
      } finally {
        this.loadingAssetBoard = false
      }
    },

    /**
     * Build filter payload for API from pending filters
     * @returns {Object} Filter payload
     */
    buildFilterPayload() {
      const payload = {}

      // Simple value filters
      const simpleFilters = ['process', 'launch', 'lastupdate']
      simpleFilters.forEach((key) => {
        const val = this.pendingFilters[key].val
        if (val && val.trim() !== '') {
          payload[key] = [val]
        } else {
          payload[key] = []
        }
      })

      // Minorities filter - boolean true or null
      payload.isminority = this.pendingFilters.minority.val || null

      // Infra filter - boolean true or null
      payload.isinfra = this.pendingFilters.infra.val || null

      // Multi-select filters (from applied state)
      const multiFilters = ['asset', 'ibank']
      multiFilters.forEach((key) => {
        payload[key] = [...this.appliedFilters[key]]
      })

      // Tree select filters
      if (this.pendingFilters.subsector.treeValue) {
        payload.subsector = this.expandTreeSelections(
          this.pendingFilters.subsector.treeValue,
          this.subsectorTreeSettings.options
        )
      } else {
        payload.subsector = []
      }

      if (this.pendingFilters.hqcountry.treeValue) {
        payload.hqcountry = [...this.pendingFilters.hqcountry.treeValue]
      } else {
        payload.hqcountry = []
      }

      // EBITDA range
      payload.ebitda = this.pendingFilters.ebitda.range

      return payload
    },

    /**
     * Expand tree selections to include children if parent is selected
     * @param {Array} selectedValues - Selected tree values
     * @param {Array} options - Tree options
     * @returns {Array} Expanded values
     */
    expandTreeSelections(selectedValues, options) {
      if (!Array.isArray(selectedValues)) return []

      const expanded = []

      selectedValues.forEach((value) => {
        const categoryIndex = options.findIndex((opt) => opt.id === value)
        const isMainCategory = categoryIndex > -1

        if (isMainCategory && options[categoryIndex].children) {
          // Add all children
          options[categoryIndex].children.forEach((child) => {
            if (!expanded.includes(child.id)) {
              expanded.push(child.id)
            }
          })
        } else {
          // Add the value itself
          if (!expanded.includes(value)) {
            expanded.push(value)
          }
        }
      })

      return expanded
    },

    /**
     * Update applied filters from pending state
     */
    updateAppliedFilters() {
      // Update multi-select applied filters (asset, ibank already updated via staging)

      // Update tree-based applied filters for display
      if (this.pendingFilters.subsector.treeValue) {
        this.appliedFilters.subsector = this.expandTreeSelections(
          this.pendingFilters.subsector.treeValue,
          this.subsectorTreeSettings.options
        )
      } else {
        this.appliedFilters.subsector = []
      }

      if (this.pendingFilters.hqcountry.treeValue) {
        this.appliedFilters.hqcountry = [
          ...this.pendingFilters.hqcountry.treeValue
        ]
      } else {
        this.appliedFilters.hqcountry = []
      }
    },

    /**
     * Clear all filters and reset to initial state
     */
    clearFilters() {
      if (this.loadingAssetBoard) return

      this.loadingAssetBoard = true
      this.listItems = []

      setTimeout(() => {
        // Reset pending filters
        Object.keys(this.pendingFilters).forEach((key) => {
          if (key === 'ebitda') {
            this.pendingFilters[key].range = [EBITDA_MIN, EBITDA_MAX]
          } else if (key === 'minority' || key === 'infra') {
            this.pendingFilters[key].val = false
          } else {
            this.pendingFilters[key].val = ''
            this.pendingFilters[key].treeValue = null
          }
        })

        // Reapply the default US Assets HQ Country filter (not user-clearable while on this tab)
        if (this.assetBoardTab === 'us') {
          this.pendingFilters.hqcountry.treeValue = [US_HQ_COUNTRY_VALUE]
        }

        // Clear staged filters
        this.clearStagedFilters()

        // Clear applied filters
        Object.keys(this.appliedFilters).forEach((key) => {
          this.appliedFilters[key] = []
        })
        if (this.assetBoardTab === 'us') {
          this.appliedFilters.hqcountry = [US_HQ_COUNTRY_VALUE]
        }

        // Reset store
        this.$store.commit('resetAssetBoardFilters', {}, true)
        if (this.assetBoardTab === 'us') {
          this.$store.commit('setAssetBoardFilters', {
            hqcountry: [US_HQ_COUNTRY_VALUE]
          })
        }
        this.$store.commit('setAssetBoardPager', { page: 1 })

        // Fetch fresh data
        this.fetchData({ monday: false })

        this.loadingAssetBoard = false
      }, 500)
    },

    /**
     * Clear all staged filter suggestions
     */
    clearStagedFilters() {
      Object.keys(this.stagedFilters).forEach((key) => {
        this.stagedFilters[key] = []
      })
    },

    /**
     * Remove a specific applied filter
     * @param {String} filterKey - Filter key (e.g., 'asset', 'ibank')
     * @param {String} value - Value to remove
     */
    removeAppliedFilter(filterKey, value) {
      if (this.loadingAssetBoard) return

      this.loadingAssetBoard = true

      // Remove from applied filters
      this.appliedFilters[filterKey] = this.appliedFilters[filterKey].filter(
        (item) => item !== value
      )

      // Update store
      const updatedFilters = { ...this.Filters }
      updatedFilters[filterKey] = [...this.appliedFilters[filterKey]]

      this.$store.commit('resetAssetBoardFilters', updatedFilters)
      this.$store.commit('setAssetBoardPager', { page: 1 })

      this.fetchData({ monday: false })
      this.loadingAssetBoard = false
    },

    // ===== STAGING METHODS FOR SEARCH INPUTS =====

    /**
     * Stage pending asset filter from search input
     * @param {String} selectedValue - Optional pre-selected value
     */
    async stagePendingAssetFilter(selectedValue = null) {
      if (this.loadingAssetBoard) return

      const valueToAdd = selectedValue || this.stagedFilters.asset[0]

      if (valueToAdd && !this.appliedFilters.asset.includes(valueToAdd)) {
        this.appliedFilters.asset.push(valueToAdd)
        this.stagedFilters.asset = []
        this.pendingFilters.asset.val = ''
      }
    },

    /**
     * Stage pending ibank filter from search input
     * @param {String} selectedValue - Optional pre-selected value
     */
    async stagePendingIBankFilter(selectedValue = null) {
      if (this.loadingAssetBoard) return

      const valueToAdd = selectedValue || this.stagedFilters.ibank[0]

      if (valueToAdd && !this.appliedFilters.ibank.includes(valueToAdd)) {
        this.appliedFilters.ibank.push(valueToAdd)
        this.stagedFilters.ibank = []
        this.pendingFilters.ibank.val = ''
      }
    },

    // ===== DATA FETCHING METHODS =====

    /**
     * Fetch asset board data from API
     * @param {Object} options - Fetch options
     * @param {Boolean} options.monday - Whether to fetch from Monday.com
     * @param {Boolean} options.notification - Whether to show notification
     */
    async fetchData({ monday = false, notification = false }) {
      try {
        this.loadingAssetBoard = true
        this.loadingStatus = 'secondary'
        this.loadingText = 'Please wait...'

        const { status, data } = await Services.Projects.Admin.getAssetBoard({
          monday,
          pager: this.Pager,
          filters: this.Filters,
          sort: this.SortOrders
        })

        if (status === 200) {
          this.loadingAssetBoard = false

          if (!data.content.list.length) {
            return
          }

          // Process EBITDA list
          this.ebitdaList = data.content.ebitdaList.map(
            ({ label: text, value }) => ({
              text,
              value
            })
          )

          // Process table data
          const processedData = this.processTableData(data.content)
          this.listItems = processedData

          this.loadingStatus = ''
          this.loadingText = ''

          if (notification) {
            this.showToast('Synced with Monday.com', 'success')
          }
        } else {
          this.loadingStatus = 'danger'
          this.loadingText =
            'Error fetching Asset Board, Please Try again later.'
          this.showToast('Error fetching Asset Board', 'danger')
          this.loadingAssetBoard = false
        }
      } catch (error) {
        this.loadingAssetBoard = false
        console.error('Error fetching data:', error)
        throw error
      }
    },

    /**
     * Fetch metadata from API
     */
    async fetchMetas() {
      try {
        const { status, data } = await Services.Projects.Admin.getMetas()

        if (status === 200) {
          const {
            assetBoard: {
              subsectors,
              assetsprocess,
              assetslastupdate,
              hqcountries,
              hqcountriesNew,
              assetslaunchtags
            }
          } = data.content.metas

          this.metas = {
            subsectors,
            assetsprocess,
            assetslastupdate,
            hqcountries,
            hqcountriesNew,
            assetslaunchtags
          }

          // Build tree options
          this.buildSubsectorTreeOptions()
          this.buildHQCountryTreeOptions()

          // Add default options to dropdowns
          this.addDefaultOptionsToDropdowns()
        }
      } catch (error) {
        console.error('Error fetching metas:', error)
      }
    },

    /**
     * Fetch from Monday.com and sync
     */
    fetchFromMonday() {
      if (this.loadingAssetBoard) return

      this.showToast('Syncing with Monday.com...', 'info', 'Please wait...')

      this.fetchData({ monday: true, notification: true })
        .then(() => {
          this.$store.commit('setAssetBoardPager', { page: 1 })
        })
        .catch(() => {
          this.showToast('Error syncing with Monday.com', 'danger')
        })
    },

    /**
     * Search assets by query
     * @param {String} query - Search query
     * @returns {Array} List of matching assets
     */
    async searchAssets(query) {
      try {
        const { status, data } = await Services.Projects.Admin.searchAssetBoard(
          query
        )

        if (status === 200) {
          const uniqueAssets = []
          data.content.list.forEach((item) => {
            if (item.asset && !uniqueAssets.includes(item.asset)) {
              uniqueAssets.push(item.asset)
            }
          })
          return uniqueAssets.slice(0, FILTER_SUGGESTION_LIMIT)
        }
        return []
      } catch (error) {
        console.error('Error searching assets:', error)
        return []
      }
    },

    /**
     * Search investment banks by query
     * @param {String} query - Search query
     * @returns {Array} List of matching ibanks
     */
    async searchIBanks(query) {
      try {
        const { status, data } = await Services.Projects.Admin.searchAssetBoard(
          query,
          'ibank'
        )

        if (status === 200) {
          const uniqueIBanks = []
          data.content.list.forEach((item) => {
            if (item.ibank && !uniqueIBanks.includes(item.ibank)) {
              uniqueIBanks.push(item.ibank)
            }
          })
          return uniqueIBanks.slice(0, FILTER_SUGGESTION_LIMIT)
        }
        return []
      } catch (error) {
        console.error('Error searching ibanks:', error)
        return []
      }
    },

    // ===== DATA PROCESSING METHODS =====

    /**
     * Process table data for display
     * @param {Object} content - API response content
     * @returns {Array} Processed table data
     */
    processTableData({ list = [], total = 0 }) {
      const processedList = list.map((item) => {
        // Parse string arrays
        if (typeof item.partners === 'string') {
          item.partners = item.partners.replace(/\{|\}|"/g, '').split(',')
        }
        if (typeof item.ems === 'string') {
          item.ems = item.ems.replace(/\{|\}|"/g, '').split(',')
        }

        // Create dynamic CSS classes for cell colors, and set cell variants.
        // Cells the API gives no colour for fall back to a single shared
        // "n/a" variant (light neutral fill, dark text — see table-view.vue)
        // rather than a per-row mid-grey class that needed white text.
        const cellColors = item?.meta?.colors ?? {}

        item._cellVariants = [
          'subsector',
          'process',
          'deck',
          'priority'
        ].reduce((variants, key) => {
          const color = cellColors[key]

          if (color) {
            this.createDynamicCSSClass(
              `table-lizard-assetboard-class-${item.uid}-${key}`,
              `background-color: ${color};`
            )
            variants[key] = `lizard-assetboard-class-${item.uid}-${key}`
          } else {
            variants[key] = 'lizard-assetboard-na'
          }

          return variants
        }, {})

        return item
      })

      // Create paginated result array
      const result = new Array(total).fill({})
      const startIndex = (this.Pager.page - 1) * this.Pager.perPage

      for (let i = 0; i < processedList.length; i++) {
        result[startIndex + i] = processedList[i]
      }

      return result
    },

    // ===== UI HELPER METHODS =====

    /**
     * Build subsector tree options
     */
    buildSubsectorTreeOptions() {
      if (!this.metas.subsectors) return

      this.subsectorTreeSettings.options = this.metas.subsectors.map(
        ({ value, text, subcategories = [] }) => {
          const option = {
            id: value,
            label: text
          }

          if (subcategories.length) {
            option.children = subcategories.map(({ value, text }) => ({
              id: value,
              label: text
            }))
          }

          return option
        }
      )
    },

    /**
     * Build HQ country tree options
     */
    buildHQCountryTreeOptions() {
      if (!this.metas.hqcountriesNew) return

      this.hqcountryTreeSettings.options = this.metas.hqcountriesNew.map(
        ({ value, text, regions = [], countries = [] }) => {
          const option = {
            id: value,
            label: text
          }

          if (regions.length) {
            option.children = regions.map((region) => ({
              id: region.value,
              label: region.text,
              children: region.countries.map((country) => ({
                id: `${region.value}:${country.value}`,
                label: country.text
              }))
            }))
          }

          if (countries.length) {
            option.children = countries.map(({ value, text }) => ({
              id: `${option.id}:${value}`,
              label: text
            }))
          }

          return option
        }
      )
    },

    /**
     * Add default options to dropdowns
     */
    addDefaultOptionsToDropdowns() {
      const dropdowns = [
        { key: 'assetsprocess', defaultText: 'Select one...' },
        { key: 'assetslastupdate', defaultText: 'Select one...' },
        { key: 'hqcountries', defaultText: 'Select one...' },
        { key: 'assetslaunchtags', defaultText: 'Select one...' }
      ]

      dropdowns.forEach(({ key, defaultText }) => {
        if (
          this.metas[key] &&
          this.metas[key].findIndex((itm) => itm.value === '') === -1
        ) {
          this.metas[key].unshift({ value: '', text: defaultText })
        }
      })

      // Add "Other" option to HQ countries
      if (
        this.metas.hqcountries &&
        this.metas.hqcountries.findIndex((itm) => itm.value === 'other') === -1
      ) {
        this.metas.hqcountries.push({ value: 'other', text: 'Other' })
      }
    },

    /**
     * Create dynamic CSS class
     * @param {String} className - Class name
     * @param {String} rules - CSS rules
     */
    createDynamicCSSClass(className, rules) {
      const style = document.createElement('style')
      const cssRule = `.${className} { ${rules} }`

      if (style.styleSheet) {
        style.styleSheet.cssText = cssRule // IE support
      } else {
        style.appendChild(document.createTextNode(cssRule))
      }

      document.head.appendChild(style)
    },

    /**
     * Get slider tooltip value with 100+ formatting
     * @param {Number} value - Slider value
     * @returns {String} Formatted value
     */
    getSliderTooltipValue(value) {
      return Number(value) === 100 ? '100+' : String(value)
    },

    /**
     * Show Bootstrap toast notification
     * @param {String} message - Toast message
     * @param {String} variant - Toast variant (success, danger, info, etc.)
     * @param {String} title - Toast title
     */
    showToast(message, variant = 'info', title = null) {
      this.$bvToast.toast(message, {
        title:
          title ||
          (variant === 'success'
            ? 'Done!'
            : variant === 'danger'
            ? 'Error'
            : 'Info'),
        variant,
        solid: true
      })
    },

    // ===== CLIENT VIEW METHODS =====

    /**
     * Toggle client view mode
     */
    toggleClientView() {
      this.prepareReservedAssets((err, assets) => {
        if (!err) {
          this.allReservedAssets = [...this.allReservedAssets, ...assets]
        }
      })
      this.clearFilters()
      this.$store.commit(
        'setAssetBoardClientView',
        !this.IsOnAssetBoardClientViewMode
      )
      setTimeout(() => {
        if (
          !this.IsOnAssetBoardClientViewMode &&
          this.allReservedAssets.length
        ) {
          // console.log('EMAIL-REQ:', this.allReservedAssets.length)
          this.sendReservedAssetsEmail(this.allReservedAssets)
        }
      }, 3000)
    },

    prepareReservedAssets(callback) {
      if (this.ReservedAssets.length) {
        const selectedAssets = this.listItems
          .filter((item) => this.SelectedAssets.includes(item.uid))
          .map((item) => {
            item.status = 'INTERESTED'
            return item
          })
        const rejectedAssets = this.listItems
          .filter((item) => this.RejectedAssets.includes(item.uid))
          .map((item) => {
            item.status = 'REJECTED'
            return item
          })
        callback(null, [...selectedAssets, ...rejectedAssets])
      } else {
        console.log('1', this.IsOnAssetBoardClientViewMode)
        console.log('2', this.ReservedAssets)
        callback(new Error('No Reserved Assets'), null)
      }
    },

    // ===== EXPORT METHODS =====

    /**
     * Generate Excel export data
     */
    async generateExcel() {
      try {
        this.exportName = `assetboard-data.${this.exportType}`

        const { status, data } = await Services.Assets.Admin.exportExcel(
          this.Filters
        )
        this.exportData = status === 200 ? data.content : []
      } catch (error) {
        console.error('Error generating Excel:', error)
      }
    },

    /**
     * Reset export data after export completion
     */
    resetExportData() {
      this.exportName = `assetboard-data.${this.exportType}`
      this.exportData = []
    },

    /**
     * Generate Excel for Reserved Assets
     */
    async generateReservedAssetsExcel() {
      try {
        this.exportName = `reserved-assets.${this.exportType}`
        const selectedAssets = this.listItems
          .filter((item) => this.SelectedAssets.includes(item.uid))
          .map((item) => {
            item.status = 'SELECTED'
            return item
          })
        const rejectedAssets = this.listItems
          .filter((item) => this.RejectedAssets.includes(item.uid))
          .map((item) => {
            item.status = 'REJECTED'
            return item
          })
        this.exportData = [...selectedAssets, ...rejectedAssets]
        this.reportEmailSent = false
      } catch (Exception) {
        console.error('Error generating Excel:', Exception)
      }
    },

    sendReservedAssetsEmail(data = []) {
      try {
        if (this.reportEmailSent || !data.length) {
          return
        }
        // console.log('ReservedAssets', data)
        Services.Assets.Admin.exportReservedAssets(data)
        this.reportEmailSent = true
        this.allReservedAssets = []
      } catch (Exception) {
        console.error('Error sending email:', Exception)
      }
    }
  },

  // ===== WATCHERS =====
  watch: {
    /**
     * Watch asset board tab switch - set/restore the HQ Country filter
     * and re-run the search automatically
     */
    assetBoardTab(newTab, oldTab) {
      if (newTab === 'us') {
        this.prevHqCountryTreeValue = this.pendingFilters.hqcountry.treeValue
        this.pendingFilters.hqcountry.treeValue = [US_HQ_COUNTRY_VALUE]
        this.applyFilters()
      } else if (oldTab === 'us') {
        this.pendingFilters.hqcountry.treeValue = this.prevHqCountryTreeValue
        this.prevHqCountryTreeValue = null
        this.applyFilters()
      }
    },

    /**
     * Watch asset search input for suggestions
     */
    'pendingFilters.asset.val': {
      async handler(newValue) {
        if (this.loadingAssetBoard) return

        if (!newValue || newValue.trim().length < 1) {
          this.stagedFilters.asset = []
          return
        }

        const suggestions = await this.searchAssets(newValue)
        this.stagedFilters.asset = suggestions.filter(
          (suggestion) => !this.appliedFilters.asset.includes(suggestion)
        )
      }
    },

    /**
     * Watch ibank search input for suggestions
     */
    'pendingFilters.ibank.val': {
      async handler(newValue) {
        if (this.loadingAssetBoard) return

        if (!newValue || newValue.trim().length < 1) {
          this.stagedFilters.ibank = []
          return
        }

        const suggestions = await this.searchIBanks(newValue)
        this.stagedFilters.ibank = suggestions.filter(
          (suggestion) => !this.appliedFilters.ibank.includes(suggestion)
        )
      }
    },

    /**
     * Watch pager changes to refetch data
     */
    Pager: {
      handler() {
        this.prepareReservedAssets((err, assets) => {
          if (!err) {
            this.allReservedAssets = [...this.allReservedAssets, ...assets]
          }
          this.fetchData({ monday: false })
        })
      },
      deep: true
    }
  }
}
</script>

<style scoped>
@import '@riophae/vue-treeselect/dist/vue-treeselect.min.css';

.bg-white {
  /* Opaque equivalent of rgba(245, 244, 239, 0.9) over the page's white
     background - kept opaque (not the rgba original) so the active tab's
     overlap fully hides the card's top border instead of letting it bleed
     through a semi-transparent fill. */
  --lizard-filter-panel-bg: rgb(246, 245, 241);
}

/* ===== BUTTON LISTS ===== */
.lizard-assetboard-btns-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.lizard-assetboard-btns-list li {
  margin: 0 2px;
  display: inline-block;
}

/* ===== ACTION BUTTONS ROW LAYOUT ===== */
.lizard-action-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

/* ===== EXIT CLIENT VIEW ROW LAYOUT ===== */
.lizard-exit-client-view-row {
  display: flex;
  justify-content: flex-end;
  padding-right: 12px;
}

/* ===== ASSET TABS =====
   Folder-style tabs that dock onto the filter panel / table below them:
   the active tab shares the panel's background and has no bottom border,
   so it visually fuses into the panel - making it obvious the tabs are
   switching the content underneath, not firing a one-off action.
   The action row uses the filter panel's own col-lg-12, so the tab strip's
   left edge lines up exactly with the panel it sits above. */
.lizard-asset-tabs-list {
  list-style: none;
  padding: 0;
  margin: 0 0 -1px 0;
  display: flex;
  align-items: flex-end;
  position: relative;
  z-index: 2;
}

.lizard-asset-tabs-list li {
  margin: 0 2px 0 0;
  display: inline-block;
}

.lizard-asset-tab {
  padding: 7px 18px;
  text-align: center;
  cursor: pointer;
  color: #999;
  background-color: #eceff1;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  white-space: nowrap;
  border: 1px solid #ced4da;
  opacity: 0.7;
  transition: color 0.15s ease, background-color 0.15s ease, opacity 0.15s ease;
}

.lizard-asset-tab:hover {
  color: #495057;
  background-color: #e2e6ea;
  opacity: 0.9;
}

.lizard-asset-tab.active {
  color: #0070c0;
  background-color: var(--lizard-filter-panel-bg);
  border-color: #ced4da;
  border-bottom: 1px solid var(--lizard-filter-panel-bg);
  padding-bottom: 8px;
  opacity: 1;
}

/* Filter panel background/border - shared with the active tab (which docks
   onto this panel) so they read as one continuous surface. */
.lizard-filter-panel-card {
  background-color: var(--lizard-filter-panel-bg) !important;
  border: 1px solid #ced4da !important;
  border-top: none !important;
}

/* ===== FILTER LABELS ===== */
.filter-label {
  font-weight: bold;
  color: #999;
  white-space: nowrap;
  display: block;
}

.filter-label-text {
  font-variant: small-caps;
}

.filter-label-hint {
  font-size: 8pt;
}

/* ===== SLIDER STYLING ===== */
.lizard-slider-tooltip {
  color: #999;
  font-size: 0.8rem;
  font-weight: bold;
  text-align: center;
}

/* ===== COLUMN PADDING ===== */
.lizard-col-padding {
  padding-left: 2px !important;
  padding-right: 2px !important;
}

/* ===== FILTER ROW - KEEP ALL FILTERS ON ONE LINE ===== */
.lizard-filters-row {
  flex-wrap: nowrap;
}

.lizard-filters-row > [class*='col'] {
  min-width: 0;
}

.lizard-filters-row .filter-label-text,
.lizard-filters-row .filter-label-hint {
  white-space: nowrap;
}

/* ===== FIXED-WIDTH TOGGLE COLUMNS (Minorities / Infra) ===== */
.lizard-toggle-col-minority {
  flex: 0 0 108px;
  max-width: 108px;
}

.lizard-toggle-col-infra {
  flex: 0 0 80px;
  max-width: 80px;
}

/* ===== FIXED-WIDTH ACTION BUTTONS COLUMN (Search / Clear) ===== */
.lizard-action-col {
  flex: 0 0 170px;
  max-width: 170px;
}

/* ===== MINORITIES RADIO FILTER ===== */
.lizard-minority-radio {
  display: flex;
  width: 100%;
  height: 31px;
}

.lizard-minority-radio >>> .btn {
  flex: 1;
  height: 31px;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 400;
  text-transform: none;
  padding: 0 4px;
  white-space: nowrap;
  border-color: #ced4da;
  color: #495057;
  background-color: #fff;
}

.lizard-minority-radio >>> .btn.active {
  background-color: #66ccff;
  border-color: #66ccff;
  color: #fff;
}

/* ===== ACTION BUTTONS ===== */
.filter-action-buttons-horizontal {
  display: flex;
  flex-direction: row;
  gap: 4px;
  align-items: center;
}

/* Search Button - Inline */
.lizard-search-btn-inline {
  margin: 0;
  padding: 0 8px;
  height: 30px;
  line-height: 30px;
  background-color: #28a745;
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
  text-align: center;
  cursor: pointer;
  display: inline-block;
  color: #fff;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  white-space: nowrap !important;
  flex: 1;
}

.lizard-search-btn-inline:hover {
  background-color: #218838;
  border-color: #1e7e34;
}

.lizard-search-btn-inline:active {
  background-color: #1e7e34;
  border-color: #1c7430;
}

/* Clear Filters Button - Inline */
.lizard-clear-filters-btn-inline {
  margin: 0;
  padding: 0 8px;
  height: 30px;
  line-height: 30px;
  background-color: #66ccff;
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
  text-align: center;
  cursor: pointer;
  display: inline-block;
  color: #fff;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  white-space: nowrap !important;
  flex: 1;
}

.lizard-clear-filters-btn-inline:hover {
  border-color: #999;
  background-color: #4db8ff;
}

.lizard-clear-filters-btn-inline:active {
  border-color: #666;
  background-color: #33a3ff;
}

/* Monday Button */
.lizard-monday-btn {
  margin: 0 2px;
  padding: 0;
  min-width: 120px !important;
  height: 30px;
  line-height: 30px;
  background-color: #fff;
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
  text-align: center;
  cursor: pointer;
  display: inline-block;
  color: #999;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  white-space: nowrap !important;
  overflow: hidden;
  text-overflow: ellipsis;
  justify-content: center;
  align-items: center;
}

.lizard-monday-btn:hover {
  border-color: #999;
}

.lizard-monday-btn:active {
  border-color: #666;
}

.lizard-monday-btn img {
  height: 30px !important;
}

/* Client View Button */
.lizard-client-view-btn {
  margin: 0 2px;
  padding: 0;
  min-width: 120px !important;
  height: 30px;
  line-height: 30px;
  background-color: #88d8f5;
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
  text-align: center;
  cursor: pointer;
  display: inline-block;
  color: #fff;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  white-space: nowrap !important;
  overflow: hidden;
  text-overflow: ellipsis;
  justify-content: center;
  align-items: center;
}

.lizard-client-view-btn:hover {
  border-color: #999;
}

.lizard-client-view-btn:active {
  border-color: #666;
}

/* Export Excel Button */
.lizard-export-excel-btn {
  margin: 0 2px;
  padding: 0;
  min-width: 120px !important;
  height: 30px;
  line-height: 30px;
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
  text-align: center;
  cursor: pointer;
  display: inline-block;
  color: #fff;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  white-space: nowrap !important;
  overflow: hidden;
  text-overflow: ellipsis;
  justify-content: center;
  align-items: center;
}

/* Disabled State */
.lizard-monday-sync {
  opacity: 0.5 !important;
  border: #666;
  cursor: default;
}

.lizard-monday-sync img {
  opacity: 0.5 !important;
}

/* Monday Button Image */
.lizard-monday-button {
  max-width: 10vw !important;
  margin: 0;
  padding: 8px 6px;
  cursor: pointer;
}

.lizard-monday-button:active {
  max-width: 90% !important;
}
</style>

<style>
/* ===== GLOBAL STYLES ===== */
*:focus {
  border: transparent !important;
  outline: none !important;
}

/* Filter Tags */
ul.filterTags {
  list-style: none;
  padding: 0;
  margin: 0;
}

ul.filterTags li {
  margin: 0 2px;
  display: inline-block;
}

/* Setting Badges */
.lizard-setting-badge {
  margin: 0;
  min-width: 60px !important;
}

.lizard-setting-badge:hover {
  cursor: pointer;
  color: #eee !important;
  opacity: 0.6 !important;
}

.lizard-setting-badge:active {
  color: #ccc !important;
  opacity: 0.8 !important;
}

.lizard-setting-badge-temp {
  opacity: 0.4 !important;
}

/* Table Input Fields */
.lizard-update-input {
  font-size: 0.8rem !important;
  font-weight: bold !important;
  border: none !important;
  background-color: transparent !important;
  padding: 0 !important;
  margin: 0 !important;
  max-width: 150px !important;
  text-align: center !important;
}

.lizard-update-select {
  font-size: 0.8rem !important;
  font-weight: bold !important;
  max-width: 150px !important;
}

/* Table Cell Styles */
.lizard-text-cell-2 {
  width: calc(100vw / 7) !important;
  min-width: calc(100vw / 7) !important;
  max-width: calc(100vw / 7) !important;
  margin: 0 !important;
  padding: 5px !important;
  overflow: auto;
}

/* Process Cell Colors */
.lizard-process-cell-finding-bidder {
  background-color: #e8697d;
}

.lizard-process-cell-hired {
  background-color: #55d392;
}

.lizard-process-cell-talk-to-owner {
  background-color: #fdbc64;
}

.lizard-process-cell-deck-done {
  background-color: #349ecd;
}

.lizard-process-cell-do-deck {
  background-color: #b57de3;
}

.lizard-process-cell-default {
  background-color: #797e93;
}

.lizard-process-cell {
  color: white;
  min-width: 130px !important;
}

.lizard-process-cell div:hover {
  cursor: pointer;
}

/* Deck Cell Colors */
.lizard-deck-cell-no-need {
  background-color: #349ecd;
}

.lizard-deck-cell-medium {
  background-color: #fdbc64;
}

.lizard-deck-cell-high {
  background-color: #e8697d;
}

.lizard-deck-cell-done {
  background-color: #55d392;
}

.lizard-deck-cell-default {
  background-color: #797e93;
}

.lizard-deck-cell {
  color: white;
  min-width: 130px !important;
}

.lizard-deck-cell div:hover {
  cursor: pointer;
}

/* EBITDA Groups */
.lizard-ebitda-groups {
  width: 100% !important;
  line-height: 1 !important;
}

.lizard-ebitda-groups label {
  background-color: #eee !important;
  color: #ccc !important;
  height: 32px !important;
  min-height: 32px !important;
  line-height: 2 !important;
  vertical-align: middle !important;
  box-sizing: border-box;
  min-width: 25% !important;
  font-size: 75% !important;
  padding: auto 0 !important;
  border: 0 !important;
  overflow: hidden;
  text-overflow: ellipsis;
}

.lizard-ebitda-groups label:hover {
  background-color: #ccc !important;
  color: #eee !important;
}

.lizard-ebitda-groups label.focus {
  box-shadow: 0 0 0 transparent !important;
}

.lizard-ebitda-groups label.active {
  background-color: #66ccff !important;
  color: #fff !important;
}

/* TreeSelect Styling */
.vue-treeselect__control {
  height: 31px !important;
  border-color: #ced4da !important;
  border-radius: 0.25rem !important;
}

.vue-treeselect__placeholder,
.vue-treeselect__single-value {
  line-height: 29px !important;
}

.vue-treeselect__placeholder {
  color: #495057 !important;
  font-weight: 400 !important;
  font-size: 0.875rem !important;
}
</style>
